import React, { memo } from "react";

/**
 * Flashcard with CSS 3D flip animation.
 * Front: HSK badge + Chinese word + pinyin + hint
 * Back:  word reminder + meaning + example
 */
const Flashcard = memo(function Flashcard({ card, isFlipped, onClick, animClass = "" }) {
  return (
    <div
      className={`w-full cursor-pointer select-none ${animClass}`}
      style={{ perspective: "1200px" }}
      onClick={onClick}
    >
      <div
        className="relative"
        style={{
          transformStyle: "preserve-3d",
          transition: "transform 0.52s cubic-bezier(0.4, 0.2, 0.2, 1)",
          transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          height: "320px",
        }}
      >
        {/* ── Front ── */}
        <div
          className="absolute inset-0 rounded-3xl shadow-xl overflow-hidden flex flex-col items-center justify-center gap-4 px-10"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            background: "linear-gradient(160deg, #ffffff 0%, #fafafa 100%)",
          }}
        >
          {/* Accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-3xl bg-gradient-to-r from-red-400 via-red-500 to-red-600" />

          {/* HSK badge */}
          {card.hskLevel && (
            <span className="absolute top-5 right-5 text-xs font-bold tracking-widest text-red-400 bg-red-50 px-2.5 py-1 rounded-xl">
              HSK {card.hskLevel}
            </span>
          )}

          {/* Word */}
          <div className="flex flex-col items-center gap-2 mt-2">
            <span className="text-6xl font-bold text-gray-900 tracking-wide leading-none">
              {card.word}
            </span>
            {card.pinyin && (
              <span className="text-xl text-red-500 font-medium tracking-wide">
                {card.pinyin}
              </span>
            )}
          </div>

          {/* Flip hint */}
          <span className="absolute bottom-5 text-xs text-gray-300 flex items-center gap-1.5">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M7 16V4m0 0L3 8m4-4 4 4" /><path d="M17 8v12m0 0 4-4m-4 4-4-4" />
            </svg>
            Nhấn để lật
          </span>
        </div>

        {/* ── Back ── */}
        <div
          className="absolute inset-0 rounded-3xl shadow-xl overflow-hidden flex flex-col items-center justify-center gap-4 px-10"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            background: "linear-gradient(160deg, #fff1f2 0%, #fde8e8 100%)",
          }}
        >
          {/* Accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1.5 rounded-t-3xl bg-gradient-to-r from-red-300 via-red-400 to-red-500" />

          {/* Word reminder top-left */}
          <span className="absolute top-5 left-5 text-sm font-bold text-red-300 tracking-wide">
            {card.word}
          </span>

          <div className="flex flex-col items-center gap-3 text-center">
            <p className="text-3xl font-bold text-gray-800 leading-snug">
              {card.meaning}
            </p>
            {card.example && (
              <p className="text-base text-gray-500 italic leading-relaxed max-w-xs border-t border-red-100 pt-3">
                {card.example}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default Flashcard;
